import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { CONTACT } from "@/i18n/translations";
import { useHome } from "@/i18n/home";

const IMGS = [
  "https://images.pexels.com/photos/29870245/pexels-photo-29870245.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  "https://images.unsplash.com/photo-1567745218240-95b39d13de05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];
const DURATION = 6000;

export const IndustryFocus = () => {
  const ih = useHome().industry;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const t = ih.tabs[active];

  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => setActive((a) => (a + 1) % IMGS.length), DURATION);
    return () => clearTimeout(id);
  }, [active, paused]);

  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-28" data-testid="industry-focus">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B87B0] mb-5">{ih.overline}</p>
        <h2 className="mb-10 font-display font-semibold tracking-tight text-4xl sm:text-5xl leading-[1.04] text-[#090A0C]">{ih.title}</h2>

        <div
          className="relative min-h-[600px] sm:min-h-0 sm:h-[600px] overflow-hidden rounded-[2rem] bg-[#090A0C]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="industry-slideshow"
        >
          <AnimatePresence mode="sync">
            <motion.img
              key={active}
              src={IMGS[active]}
              alt={t.title}
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ opacity: 0, scale: 1.12 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 0.9 }, scale: { duration: DURATION / 1000 + 1, ease: "linear" } }}
            />
          </AnimatePresence>
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(9,10,12,0.85) 0%, rgba(9,10,12,0.45) 55%, rgba(9,10,12,0.1) 100%)" }} />

          <div className="relative z-10 flex h-full flex-col justify-end p-7 sm:p-12">
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5 }} className="max-w-2xl">
                <span className="font-mono text-sm font-bold text-[#00C3FF]">0{active + 1} / 0{IMGS.length}</span>
                <h3 className="mt-3 font-display text-3xl sm:text-5xl font-bold tracking-tight text-white" data-testid="industry-slide-title">{t.title}</h3>
                <div className="mt-6 flex flex-wrap gap-2">
                  {t.points.map((p) => (
                    <span key={p} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-xs sm:text-sm text-white backdrop-blur-md">
                      <Check size={14} color="#00C3FF" strokeWidth={3} /> {p}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="industry-cta" className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-[#090A0C] transition-transform hover:scale-[1.03]" style={{ background: "var(--pt-blue)" }}>
              {ih.cta} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="industry-tabs">
              {ih.tabs.map((tab, i) => (
                <button key={tab.label} onClick={() => setActive(i)} data-testid={`industry-tab-${i}`} className="group text-left">
                  <span className="block h-1 w-full overflow-hidden rounded-full bg-white/20">
                    {i === active && (
                      <motion.span
                        key={`${active}-${paused}`}
                        className="block h-full rounded-full bg-[#00C3FF]"
                        initial={{ width: paused ? "100%" : "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: paused ? 0 : DURATION / 1000, ease: "linear" }}
                      />
                    )}
                  </span>
                  <span className={`mt-2.5 block text-sm font-semibold transition-colors ${i === active ? "text-white" : "text-white/50 group-hover:text-white/80"}`}>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
